import { Select, Stack } from '@mantine/core'
import { DateInput } from '@mantine/dates'
import { useForm, zodResolver } from '@mantine/form'
import { ContextModalProps } from '@mantine/modals'
import Form from 'common/Form'
import NumberWithUnitInput from 'common/inputs/NumberWithUnitInput'
import { useUpdateShortageOrder } from 'hooks/shortage'
import { useMemo, useState } from 'react'
import { updateShortageOrderSchema } from 'types/schemas'
import { ShortageOrderTableItem, ShortageStatus } from 'types/shortage'
import { getShortageOrderStatus, shortageOrderStatusOptions, trimFormValues } from 'utils'
import z from 'zod'

type Props = {
  shortageOrder: ShortageOrderTableItem
}

const updateShortageOrderSchemaForType = updateShortageOrderSchema(0, ShortageStatus.FULFILLED)
type FormValues = z.infer<typeof updateShortageOrderSchemaForType>

export default function UpdateShortageOrderModal({
  context,
  id,
  innerProps: { shortageOrder },
}: ContextModalProps<Props>) {
  const updateShortageOrder = useUpdateShortageOrder()
  const originalShortageOrder = useMemo(() => shortageOrder, [shortageOrder])

  const [status, setStatus] = useState<ShortageStatus>(getShortageOrderStatus(shortageOrder))

  async function onSubmit(formValues: FormValues) {
    await updateShortageOrder.mutateAsync({
      id: shortageOrder.id,
      fulfilledAt: formValues.fulfilledAt,
      fulfilledAmount: status === ShortageStatus.UNFULFILLED ? -1 : formValues.fulfilledAmount,
      orderedAmount: formValues.orderedAmount,
      isEmailSent: formValues.isEmailSent,
    })
  }

  const form = useForm<FormValues>({
    validate: (values) => zodResolver(updateShortageOrderSchema(shortageOrder.shortageAmount, status))(values),
    initialValues: {
      fulfilledAt: shortageOrder.fulfilledAt,
      fulfilledAmount: shortageOrder.fulfilledAmount,
      orderedAmount: shortageOrder.orderedAmount,
      unit: shortageOrder.unit,
      isEmailSent: shortageOrder.isEmailSent,
    },
    transformValues: trimFormValues,
  })

  return (
    <Form onSubmit={onSubmit} form={form} onCancel={() => context.closeModal(id)} submitText="Edytuj zamówienie">
      <Stack gap="xs">
        <Select
          label="Status"
          description="Status zamówienia. W zależności od statusu, inne pola mogą być wymagane do wypełnienia."
          data={shortageOrderStatusOptions}
          value={status}
          onChange={(value) => {
            if (!value) return
            const newStatus = value as ShortageStatus
            const previousStatus = status
            setStatus(newStatus)
            switch (newStatus) {
              case ShortageStatus.NEW:
                form.setFieldValue('isEmailSent', false)
                form.setFieldValue('fulfilledAt', null)
                form.setFieldValue('fulfilledAmount', null)
                break
              case ShortageStatus.ORDERED:
                form.setFieldValue('isEmailSent', true)
                form.setFieldValue('fulfilledAt', null)
                form.setFieldValue('fulfilledAmount', null)
                break
              case ShortageStatus.FULFILLED:
                form.setFieldValue('isEmailSent', true)
                form.setFieldValue(
                  'fulfilledAt',
                  form.values.fulfilledAt ?? originalShortageOrder.fulfilledAt ?? new Date()
                )
                if (previousStatus === ShortageStatus.UNFULFILLED) {
                  form.setFieldValue('fulfilledAmount', form.values.orderedAmount)
                } else {
                  form.setFieldValue('fulfilledAmount', originalShortageOrder.fulfilledAmount)
                }
                break
              case ShortageStatus.UNFULFILLED:
                form.setFieldValue('isEmailSent', true)
                form.setFieldValue(
                  'fulfilledAt',
                  form.values.fulfilledAt ?? originalShortageOrder.fulfilledAt ?? new Date()
                )
                form.setFieldValue('fulfilledAmount', null)
                break
            }
          }}
          withAsterisk
        />
        <NumberWithUnitInput
          label="Zamawiana ilość"
          description="Ilość produktu jaką chcesz zamówić. Musi być większa niż suma braków."
          availableUnits={[shortageOrder.unit]}
          form={form}
          amountFieldName="orderedAmount"
          withAsterisk
        />
        {(status === ShortageStatus.FULFILLED || status === ShortageStatus.UNFULFILLED) && (
          <DateInput
            label="Data realizacji"
            description="Data, kiedy zamówienie zostało zrealizowane"
            locale="pl"
            valueFormat="DD.MM.YYYY"
            placeholder="Wybierz datę realizacji"
            withAsterisk
            {...form.getInputProps('fulfilledAt')}
          />
        )}
        {status === ShortageStatus.FULFILLED && (
          <NumberWithUnitInput
            label="Zrealizowana ilość"
            description="Ilość produktu jaka została dostarczona przez producenta."
            availableUnits={[shortageOrder.unit]}
            form={form}
            amountFieldName="fulfilledAmount"
          />
        )}
      </Stack>
    </Form>
  )
}
